import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentDraft } from "@nemo/agent-schema";
import { Api, ApiError, type AgentDetail, type Issue } from "./api.js";
import { fingerprint, toDraft } from "./draft.js";

export type SaveState =
  | { kind: "saved"; at: number }
  | { kind: "saving" }
  | { kind: "pending" }
  | { kind: "error"; message: string; retryable: boolean }
  | { kind: "conflict"; message: string };

interface Options {
  api: Api;
  agentId: string;
  initial: AgentDetail;
  delayMs?: number;
}

/**
 * Keeps the form and the server in step. The form is the source of truth on screen: a failed save never
 * touches it, and the same change is retried until it goes through.
 */
export function useAutosave({ api, agentId, initial, delayMs = 800 }: Options) {
  const [form, setForm] = useState<AgentDraft>(initial.config);
  const [detail, setDetail] = useState<AgentDetail>(initial);
  const [state, setState] = useState<SaveState>({ kind: "saved", at: Date.now() });
  const [serverIssues, setServerIssues] = useState<Issue[]>([]);
  const saved = useRef(fingerprint(toDraft(initial.config)));
  const revision = useRef(initial.revision);
  const latest = useRef(form);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const force = useRef(false);

  /** Saves run one after another; each reads the latest form when it starts, so nothing is skipped or reordered. */
  const chain = useRef<Promise<void>>(Promise.resolve());
  const doSave = useCallback(async () => {
    const draft = toDraft(latest.current);
    if (fingerprint(draft) === saved.current) {
      setState((s) => (s.kind === "pending" || s.kind === "saving" ? { kind: "saved", at: Date.now() } : s));
      return;
    }
    setState({ kind: "saving" });
    try {
      const rev = force.current ? undefined : revision.current;
      const res = await api.saveAgent(agentId, draft, rev);
      force.current = false;
      revision.current = res.revision;
      saved.current = fingerprint(draft);
      setDetail(res);
      setServerIssues([]);
      setState({ kind: "saved", at: Date.now() });
    } catch (e) {
      if (e instanceof ApiError && e.code === "revision_conflict") setState({ kind: "conflict", message: e.message });
      else if (e instanceof ApiError && e.status === 400) {
        setServerIssues(e.issues);
        setState({ kind: "error", message: "Some values were not accepted. Fix the highlighted fields.", retryable: false });
      } else {
        setState({ kind: "error", message: e instanceof ApiError ? e.message : "Saving failed.", retryable: true });
        // keep trying in the background; the form on screen is untouched
        clearTimeout(timer.current);
        timer.current = setTimeout(() => void flush(), 5000);
      }
    }
  }, [api, agentId]);
  const flush = useCallback((): Promise<void> => {
    clearTimeout(timer.current);
    chain.current = chain.current.then(doSave, doSave);
    return chain.current;
  }, [doSave]);

  const update = useCallback(
    (fn: (f: AgentDraft) => AgentDraft) => {
      setForm((prev) => {
        const next = fn(prev);
        latest.current = next;
        return next;
      });
      setState((s) => (s.kind === "conflict" ? s : { kind: "pending" }));
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), delayMs);
    },
    [flush, delayMs],
  );

  // Warn before leaving with unsaved work, and save when the tab is hidden.
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (state.kind === "pending" || state.kind === "saving" || state.kind === "error") e.preventDefault();
    };
    const hidden = () => {
      if (document.visibilityState === "hidden") void flush();
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("visibilitychange", hidden);
    };
  }, [state.kind, flush]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return {
    form,
    update,
    detail,
    state,
    serverIssues,
    saveNow: flush,
    /** After a conflict: keep what is on screen and overwrite the other change. */
    overwrite: () => {
      force.current = true;
      void flush();
    },
    /** After a conflict: drop the screen's changes and load the server's version. */
    reload: async () => {
      const fresh = await api.getAgent(agentId);
      revision.current = fresh.revision;
      latest.current = fresh.config;
      saved.current = fingerprint(toDraft(fresh.config));
      setForm(fresh.config);
      setDetail(fresh);
      setState({ kind: "saved", at: Date.now() });
    },
    /** Replace server-derived state after publish/unpublish. */
    setDetail,
    syncRevision: (d: AgentDetail) => {
      revision.current = d.revision;
      setDetail(d);
    },
  };
}
